import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-object-caret>` says about itself.
 *
 * IT IS HALF OF SOMETHING, AND ITS HALF IS THE MENU. There is no trigger here. The element renders
 * nothing at all where it is placed (`:host { display: none }`); everything visible is a panel
 * portaled to `document.body`, opened by somebody calling `openAt` with a RECTANGLE. The other half
 * lives on the workspace tab row: each object tab draws its own little caret and raises `tab-caret`
 * carrying the object and that caret's live screen rect, and the shell — which owns ONE of these and
 * reuses it for every tab — fetches that object's recents and list views, sets them here, and calls
 * `openAt`. So both examples below declare a host: without one, the piece is a control that opens
 * nothing, and an example of that would teach that the piece does nothing.
 *
 * `openAt` TAKES A RECT, NOT AN ELEMENT. That is deliberate and it is why it is listed under
 * `methods` with its argument spelled out: the thing being anchored to is a caret button inside
 * another component's shadow root, which the shell has no handle on — it has the rect the event
 * carried, and that is enough.
 *
 * WHY THERE ARE TWO EXAMPLES. The panel's shape changes with the data: with no recents the
 * "Recently Viewed" section is not drawn at all, and the menu is list views only. That is the
 * first thing a person sees on an object they have never opened, so it gets its own example rather
 * than a sentence.
 */
const docs: ComponentDocs = {
  aliases: ['tab dropdown', 'object menu', 'list view picker', 'recent records menu', 'chevron menu'],

  answers: [
    'jump straight to a record I was just looking at',
    'switch to another list view of an object',
    'reach an object’s views without loading its list first',
    'show what somebody can do with an object from its tab',
  ],

  // A part of the workspace frame: one instance, owned by the shell, serving the tab row. It is not
  // something a page places — a page that wants a menu of actions wants OverflowMenu.
  placements: ['seam'],

  inputs: [
    {
      name: 'recents',
      // No `attribute`: a list of records cannot ride an attribute. It is set as a property.
      type: 'json',
      default: [],
      description:
        'The object’s recently-viewed records, as `{ id, label, href }`. An empty list omits the ' +
        '“Recently Viewed” section entirely rather than drawing an empty one.',
    },
    {
      name: 'views',
      type: 'json',
      description:
        'The object’s list views, as `{ id, label, href }` — the built-in “All …” plus whatever this ' +
        'person has saved. The “List Views” section is always drawn, even when the list is empty.',
    },
  ],

  events: [
    {
      name: 'caret-navigate',
      when: 'A record or a list view was chosen. The menu closes itself; going there is the host’s half.',
      carries: '`{ href }` — the route the chosen row named.',
    },
    {
      name: 'caret-close',
      when: 'The menu was dismissed — by Escape, by a click outside it, or by choosing a row.',
      carries: 'nothing',
    },
  ],

  // No `<slot>` is rendered. The host renders nothing and the panel is built in a portal.
  state: [],

  slots: [],

  tokens: [
    { token: '--caos-color-surface', controls: 'The panel’s background.' },
    { token: '--caos-color-surface-2', controls: 'A row’s fill on hover, and on keyboard focus.' },
    { token: '--caos-color-border', controls: 'The panel’s outline.' },
    { token: '--caos-color-text', controls: 'The record and list-view labels.' },
    { token: '--caos-color-text-muted', controls: 'The “Recently Viewed” and “List Views” section labels and their glyphs.' },
    { token: '--caos-focus-ring', controls: 'The ring on a row reached by keyboard.' },
    { token: '--caos-font', controls: 'The typeface inside the panel.' },
    { token: '--caos-font-mono', controls: 'The two section labels.' },
    { token: '--caos-radius', controls: 'The panel’s corner radius.' },
    { token: '--caos-radius-sm', controls: 'The corner radius of a row’s hover and focus fill.' },
    { token: '--caos-shadow-float', controls: 'The shadow that lifts the panel off the page.' },
  ],

  // No named seams. Nothing in the shadow tree carries a `part`, so the caret and the panel it opens
  // cannot be reached from an outside stylesheet.
  parts: [],

  methods: [
    {
      name: 'openAt',
      description:
        'Open the menu under a caret at its live screen rectangle. Re-renders the panel from whatever ' +
        '`recents` and `views` currently hold, clamps itself into the viewport, and follows the anchor ' +
        'while the page scrolls or the window resizes.',
      args: [
        {
          name: 'anchor',
          type: '{ left: number; bottom: number }',
          description:
            'Where the caret is on screen right now. A DOMRect satisfies it, which is what the tab row ' +
            'sends on `tab-caret` — the shell passes the rect straight through.',
        },
      ],
    },
    {
      name: 'hide',
      description:
        'Close the menu. Idempotent, and raises `caret-close` so the host can drop whatever state it was ' +
        'holding about which tab was open.',
      args: [
        {
          name: 'restoreFocus',
          type: 'boolean',
          description:
            'Optional, default false. When true, focus goes back to whatever held it when the menu opened — ' +
            'the caret on the tab — as it does when somebody presses Escape or chooses a row.',
        },
      ],
    },
  ],

  placing:
    'It is not placed on a page at all. The shell keeps ONE anywhere in the workspace frame — it draws ' +
    'nothing where it sits — and points it at whichever tab’s caret was just pressed. What it needs around ' +
    'it is a caller: something that knows which object the caret belonged to, can fetch that object’s ' +
    'recents and views, and has the rect to open against.',

  whenToUse:
    'Only as the tab row’s per-object menu. If you want a dropdown of your own somewhere on a page, this is ' +
    'not it — this piece has no trigger and expects a caller to drive it.',

  insteadUse: [
    { name: 'OverflowMenu', when: 'You want a menu of ACTIONS with its own trigger, on a record or a row.' },
    { name: 'WorkspaceTabs', when: 'You want the tab row itself — it is the thing that raises the event this menu answers.' },
    { name: 'AppLauncher', when: 'The list is of apps and objects to move between, not of one object’s records and views.' },
  ],

  accessibility:
    'The panel is a `role="menu"` and every row is a real `role="menuitem"` button, so rows are reachable and ' +
    'activatable from the keyboard, the section glyphs are decorative, and Escape or a click outside closes ' +
    'the menu and reports it. Focus moves to the first row when it opens, Tab and Shift+Tab stay inside the ' +
    'menu and wrap at either end, and Escape or choosing a row hands focus back to whatever held it when the ' +
    'menu opened — the caret on the tab; a click outside leaves focus where the click put it. What is left to ' +
    'the caller is the part that needs the trigger: arrow keys do not roam the rows, and the caret that ' +
    'opened it is where `aria-expanded` would have to be kept in step.',

  examples: [
    {
      key: 'recents-and-views',
      label: 'Recents and views',
      summary:
        'The full menu for an object somebody has been working in: the records they saw most recently, then ' +
        'that object’s list views.',
      placement: [
        { component: 'caos-button', inputs: { variant: 'secondary' }, children: ['Accounts ⌄'] },
        {
          component: 'caos-object-caret',
          inputs: {
            recents: [
              { id: 'a1', label: 'Meridian Supply', href: '/sales-atlas/account/a1' },
              { id: 'a2', label: 'Calder & Finch', href: '/sales-atlas/account/a2' },
            ],
            views: [
              { id: 'v1', label: 'All Accounts', href: '/sales-atlas/account?view=all' },
              { id: 'v2', label: 'My Accounts', href: '/sales-atlas/account?view=mine' },
            ],
          },
        },
      ],
      stage: { opensBelow: 196 }, // measured: 182px panel + 6px gap
      host: {
        summary:
          'The surrounding page stands in for the tab row: the button plays the tab’s caret, and pressing it ' +
          'opens this menu at that button’s own screen rectangle — exactly what the shell does.',
        run: (stage) => {
          const tab = stage.querySelector('caos-button');
          const caret = stage.querySelector('caos-object-caret');
          if (!tab || !caret) return;
          // `openAt` anchors to a RECT, not to an element — the shell hands it the caret's own rect.
          tab.addEventListener('click', () => caret.openAt(tab.getBoundingClientRect()));
        },
      },
    },
    {
      key: 'views-only',
      label: 'Views only',
      summary:
        'The same menu for an object nobody has opened yet: with no recents the “Recently Viewed” section is ' +
        'not drawn, so the menu is list views alone.',
      placement: [
        { component: 'caos-button', inputs: { variant: 'secondary' }, children: ['Orders ⌄'] },
        {
          component: 'caos-object-caret',
          inputs: {
            recents: [],
            views: [
              { id: 'v1', label: 'All Orders', href: '/sales-atlas/order?view=all' },
              { id: 'v2', label: 'Open Orders', href: '/sales-atlas/order?view=open' },
            ],
          },
        },
      ],
      // The same reservation as the full menu. This panel is shorter — a section and two rows lighter —
      // and a reservation with room to spare only leaves the stage a little taller than it needs to be.
      stage: { opensBelow: 196 },
      host: {
        summary: 'The surrounding page plays the tab row again: the button is the caret, and pressing it opens the menu at its rectangle.',
        run: (stage) => {
          const tab = stage.querySelector('caos-button');
          const caret = stage.querySelector('caos-object-caret');
          if (!tab || !caret) return;
          tab.addEventListener('click', () => caret.openAt(tab.getBoundingClientRect()));
        },
      },
    },
  ],
};

export default docs;
